import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  User,
  CreateUserDto,
  UpdateUserDto,
  QueryUserDto,
  ApiResponse,
} from '../types/user';

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: QueryUserDto) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

// API functions
const fetchUserList = async (query: QueryUserDto): Promise<ApiResponse<User[]>> => {
  const params = new URLSearchParams();
  if (query.search) params.append('search', query.search);
  if (query.role) params.append('role', query.role);
  if (query.isActive !== undefined) params.append('isActive', query.isActive.toString());
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  const response = await apiClient.get(`/users?${params.toString()}`);
  return response.data;
};

const fetchUser = async (id: string): Promise<ApiResponse<User>> => {
  const response = await apiClient.get(`/users/${id}`);
  return response.data;
};

const createUser = async (data: CreateUserDto): Promise<ApiResponse<User>> => {
  const response = await apiClient.post('/users', data);
  return response.data;
};

const updateUser = async ({ id, data }: { id: string; data: UpdateUserDto }): Promise<ApiResponse<User>> => {
  const response = await apiClient.patch(`/users/${id}`, data);
  return response.data;
};

const deleteUser = async (id: string): Promise<void> => {
  await apiClient.delete(`/users/${id}`);
};

// React Query Hooks

export function useUserList(query: QueryUserDto = {}) {
  return useQuery({
    queryKey: userKeys.list(query),
    queryFn: () => fetchUserList(query),
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => fetchUser(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(data.data.id) });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}
